// What a right-click (or a pill) can do to one item or a selection. Shared by the list,
// the board and the item page so every entry point offers the same choices.
import { Archive, ArchiveRestore, ArrowDownToLine, ArrowUpToLine, CircleDot, Combine, ExternalLink, Flag, ImagePlus, Layers, Link2, ListTree, Rocket, Shapes, User, Zap } from 'lucide-react';
import { AREAS } from '../feedback/types';
import type { MenuEntry } from '../../ui';
import {
  canSchedule, KIND_LABEL, KINDS, levelOf, PRIORITIES, PRIORITY_LABEL, STATUS_LABEL, STATUSES, isShipped, type BacklogData, type Item,
} from './types';

export type Field = 'status' | 'priority' | 'release' | 'owner' | 'kind' | 'area' | 'epic';
export type SetFields = Partial<Pick<Item, 'status' | 'priority' | 'owner_id' | 'kind' | 'area' | 'parent_id'>> & { release_id?: string | null };

export type Handlers = {
  update: (ids: string[], set: SetFields) => void;
  archive: (ids: string[], on: boolean) => void;
  move: (id: string, to: 'top' | 'bottom') => void;
  merge: (ids: string[], intoId: string) => void;
  open: (id: string, newTab?: boolean) => void;
  attach: (id: string) => void;
  copy: (text: string, what: string) => void;
};

const same = <K extends keyof Item>(items: Item[], key: K, value: Item[K]) => items.every((i) => i[key] === value);

export function fieldEntries(field: Field, targets: Item[], data: BacklogData, h: Handlers): MenuEntry[] {
  const ids = targets.map((t) => t.id);
  if (field === 'status') return STATUSES.map((s) => ({ label: STATUS_LABEL[s], checked: same(targets, 'status', s), onSelect: () => h.update(ids, { status: s }) }));
  if (field === 'priority') return PRIORITIES.map((p) => ({ label: PRIORITY_LABEL[p], checked: same(targets, 'priority', p), onSelect: () => h.update(ids, { priority: p }) }));
  if (field === 'kind') return KINDS.map((k) => ({ label: KIND_LABEL[k], checked: same(targets, 'kind', k), onSelect: () => h.update(ids, { kind: k }) }));
  if (field === 'area') return [{ label: 'Not set', checked: same(targets, 'area', null), onSelect: () => h.update(ids, { area: null }) },
    ...AREAS.map(([v, l]) => ({ label: l, checked: same(targets, 'area', v), onSelect: () => h.update(ids, { area: v }) }))];
  if (field === 'owner') {
    const me = data.team.find((p) => p.id === data.me);
    return [
      ...(me ? [{ label: `Me (${me.name})`, checked: same(targets, 'owner_id', me.id), onSelect: () => h.update(ids, { owner_id: me.id }) }] : []),
      ...data.team.filter((p) => p.id !== data.me).map((p) => ({ label: p.name, checked: same(targets, 'owner_id', p.id), onSelect: () => h.update(ids, { owner_id: p.id }) })),
      { kind: 'separator' as const },
      { label: 'Unassigned', checked: same(targets, 'owner_id', null), onSelect: () => h.update(ids, { owner_id: null }) },
    ];
  }
  if (field === 'epic') {
    const epics = data.epics ?? [];
    return [
      { kind: 'header' as const, label: 'Move to epic' },
      ...epics.map((e) => ({ label: `${e.reference} · ${e.title}`, checked: same(targets, 'parent_id', e.id), onSelect: () => h.update(ids, { parent_id: e.id }) })),
      ...(epics.length ? [] : [{ label: 'No epics yet', disabled: true }]),
      { kind: 'separator' as const },
      { label: 'No epic', checked: targets.every((t) => !t.parent_id), onSelect: () => h.update(ids, { parent_id: null }) },
    ];
  }
  const releases = data.releases.filter((r) => r.kind === 'release');
  return [
    { kind: 'header', label: 'Ship soon, quietly' },
    { label: 'Next hotfix', icon: <Zap size={14} />, checked: same(targets, 'release_id', data.hotfix_box_id), onSelect: () => h.update(ids, { release_id: data.hotfix_box_id }) },
    { kind: 'separator' },
    { kind: 'header', label: 'Release' },
    ...releases.map((r) => ({ label: `${r.name}${r.target_date ? ` · ${r.target_date.slice(5).replace('-', '/')}` : ''}`, checked: same(targets, 'release_id', r.id), onSelect: () => h.update(ids, { release_id: r.id }) })),
    ...(releases.length ? [] : [{ label: 'No open releases yet', disabled: true }]),
    { kind: 'separator' },
    { label: 'Not scheduled', checked: same(targets, 'release_id', null), onSelect: () => h.update(ids, { release_id: null }) },
  ];
}

export function itemEntries(targets: Item[], data: BacklogData, h: Handlers): MenuEntry[] {
  const one = targets.length === 1 ? targets[0]! : null;
  const ids = targets.map((t) => t.id);
  const shipped = targets.some(isShipped);
  const archived = targets.every((t) => t.archived_at);
  const sub = (f: Field) => fieldEntries(f, targets, data, h);
  const refs = targets.map((t) => t.reference).join(', ');
  return [
    { kind: 'header', label: one ? `${one.reference} · ${one.title.slice(0, 32)}${one.title.length > 32 ? '…' : ''}` : `${targets.length} selected items` },
    ...(one ? [
      { label: 'Open', icon: <ExternalLink size={14} />, onSelect: () => h.open(one.id) },
      { label: 'Open in a new tab', icon: <ExternalLink size={14} />, onSelect: () => h.open(one.id, true) },
      { kind: 'separator' as const },
    ] : []),
    { label: 'Status', icon: <CircleDot size={14} />, items: sub('status') },
    { label: 'Priority', icon: <Flag size={14} />, items: sub('priority') },
    { label: 'Ships in', icon: <Rocket size={14} />, items: sub('release'), disabled: shipped || !targets.every(canSchedule) },
    { label: 'Epic', icon: <Layers size={14} />, items: sub('epic'), disabled: !targets.every((t) => levelOf(t) === 'story') },
    { label: 'Assign to', icon: <User size={14} />, items: sub('owner') },
    { label: 'Type', icon: <Shapes size={14} />, items: sub('kind') },
    { label: 'Area', icon: <ListTree size={14} />, items: sub('area') },
    { kind: 'separator' },
    ...(one ? [{ label: 'Add screenshot or file…', icon: <ImagePlus size={14} />, onSelect: () => h.attach(one.id) }] : []),
    { label: one ? 'Copy link' : 'Copy links', icon: <Link2 size={14} />, onSelect: () => h.copy(targets.map((t) => `${window.location.origin}/admin/backlog/${t.id}`).join('\n'), one ? 'Link' : 'Links') },
    { label: one ? `Copy ${one.reference}` : 'Copy IDs', onSelect: () => h.copy(refs, one ? 'ID' : 'IDs') },
    ...(!archived ? [
      { label: 'Move to top', icon: <ArrowUpToLine size={14} />, onSelect: () => targets.slice().reverse().forEach((t) => h.move(t.id, 'top')) },
      { label: 'Move to bottom', icon: <ArrowDownToLine size={14} />, onSelect: () => targets.forEach((t) => h.move(t.id, 'bottom')) },
    ] : []),
    ...(targets.length > 1 ? [{ label: 'Merge into one item', icon: <Combine size={14} />, items: mergeEntries(targets, h) }] : []),
    { kind: 'separator' },
    archived
      ? { label: 'Restore', icon: <ArchiveRestore size={14} />, onSelect: () => h.archive(ids, false) }
      : { label: 'Archive', icon: <Archive size={14} />, danger: true, onSelect: () => h.archive(ids, true) },
  ];
}

/** Pick the item to keep; the others merge into it. */
export function mergeEntries(targets: Item[], h: Handlers): MenuEntry[] {
  const ids = targets.map((t) => t.id);
  return [{ kind: 'header', label: 'Keep this one' },
    ...targets.map((t) => ({ label: `${t.reference} · ${t.title.slice(0, 36)}`, onSelect: () => h.merge(ids.filter((x) => x !== t.id), t.id) }))];
}

/** "BL-0042 moved to In progress", "3 items: priority High". */
export function describe(targets: Item[], set: SetFields, data: BacklogData): string {
  const who = targets.length === 1 ? targets[0]!.reference : `${targets.length} items`;
  if (set.status) return `${who} moved to ${STATUS_LABEL[set.status]}`;
  if (set.priority) return `${who}: priority ${PRIORITY_LABEL[set.priority]}`;
  if (set.kind) return `${who} is now a ${KIND_LABEL[set.kind].toLowerCase()}`;
  if (set.owner_id !== undefined) return `${who} assigned to ${data.team.find((p) => p.id === set.owner_id)?.name ?? 'nobody'}`;
  if (set.release_id !== undefined) {
    const r = data.releases.find((x) => x.id === set.release_id);
    return `${who} ${set.release_id === null ? 'taken out of its release' : r?.kind === 'hotfix' ? 'added to the next hotfix' : `scheduled for ${r?.name ?? 'the release'}`}`;
  }
  if (set.area !== undefined) return `${who}: area updated`;
  if (set.parent_id !== undefined) {
    const epic = data.epics?.find((e) => e.id === set.parent_id);
    return set.parent_id === null ? `${who} taken out of its epic` : `${who} moved to ${epic ? epic.title : 'the epic'}`;
  }
  return `${who} updated`;
}
